import CurrencyInput from "../Form/CurrencyInput";
import { Modal } from "../Modal";
import { FaCashRegister } from "react-icons/fa6";
import { Formik, Form } from "formik";

import * as Yup from "yup";
import "./modern-styles.css";
import { formatPrice } from "../../utils/formatPrice";
import { formatPriceWithSymbol } from "../../utils/currency";
import { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import Loading from "react-loading";

import { toast } from "react-toastify";
import { getLastCaixaSaldoFinal, addLancamentoCaixa, getEmpresa } from "../../services/api";
import { convertPrice } from "../../utils/convertPrice";
import ModalDiferencaSaldo from "../Caixa/Modals/ModalDiferencaSaldo";
import { getStoredUser } from '../../utils/safeStorage';

const getInitialValues = (initialPrice) => ({
  amount: formatPriceWithSymbol(initialPrice),
});

export function ModalOpenCaixa({
  isOpen,
  onClose,
  initialPrice,
  handleOpen,
  refetch,
}) {
  const { t } = useTranslation('cashRegister');
  const user = getStoredUser();
  const userID = user?._id;

  const validationSchema = Yup.object({
    amount: Yup.string().required(t('openCaixa.validation.amountRequired')),
  });

  const [initialValues, setInitialValues] = useState(
    getInitialValues(initialPrice)
  );

  const [isSubmiting, setIsSubmiting] = useState(false);
  const [saldoAnterior, setSaldoAnterior] = useState(0);
  const [showDiferencaModal, setShowDiferencaModal] = useState(false);
  const [diferencaData, setDiferencaData] = useState(null);
  const [pendingOpenData, setPendingOpenData] = useState(null);

  // Buscar saldo final do último caixa quando o modal abrir
  useEffect(() => {
    if (isOpen) {
      buscarSaldoAnterior();
    }
  }, [isOpen]);

  useEffect(() => {
    setInitialValues(getInitialValues(saldoAnterior || initialPrice));
  }, [saldoAnterior, initialPrice]);

  const buscarSaldoAnterior = async () => {
    try {
      // V2: Não precisa mais enviar idEmpresa - usa token automaticamente
      const response = await getLastCaixaSaldoFinal();
      const saldoFinal = response.data.saldo_final || 0;
      setSaldoAnterior(saldoFinal);

      // Atualizar valores iniciais com o saldo anterior
      setInitialValues(getInitialValues(saldoFinal));
    } catch (error) {
      process.env.REACT_APP_ISDEVELOP === 'true' && console.error('Erro ao buscar saldo anterior:', error);
      setSaldoAnterior(0);
    }
  };

  const handleSubmit = async (values) => {
    const valorInformado = convertPrice(values.amount);
    const diferenca = valorInformado - saldoAnterior;

    // Se valor é MAIOR, abrir automaticamente com suprimento
    if (diferenca > 0 && saldoAnterior > 0) {
      process.env.REACT_APP_ISDEVELOP === 'true' && console.log(`Valor maior detectado: ${formatPriceWithSymbol(valorInformado)} > ${formatPriceWithSymbol(saldoAnterior)} (diferença: +${formatPriceWithSymbol(diferenca)})`);
      await abrirCaixa(values); // Abrir com o valor informado
      return;
    }

    // Se valor é MENOR, mostrar modal de justificativa (comportamento atual)
    if (diferenca < 0 && saldoAnterior > 0) {
      setDiferencaData({
        diferenca,
        saldoAnterior,
        saldoInformado: valorInformado,
        tipo: 'sangria'
      });

      // Abrir com o saldo anterior, não com o valor informado
      const valuesCorrigidos = {
        ...values,
        amount: formatPrice(saldoAnterior)
      };

      setPendingOpenData(valuesCorrigidos);
      setShowDiferencaModal(true);
      return;
    }

    // Se não há diferença, abrir normalmente
    await abrirCaixa(values);
  };

  const abrirCaixa = async (values, lancamentoAdicional = null) => {
    try {
      setIsSubmiting(true);

      // Abrir o caixa
      const response = await handleOpen(values);

      // Se há lançamento adicional (sangria/suprimento), criar após abertura
      if (lancamentoAdicional) {
        await criarLancamentoAutomatico(lancamentoAdicional);
      }

      onClose();

      // Mensagem personalizada para suprimento automático
      if (response?.data?.suprimento_automatico) {
        const valorSuprimento = response.data.valor_suprimento;
        toast(t('openCaixa.toasts.autoSupply', { amount: formatPriceWithSymbol(valorSuprimento) }), {
          type: "success",
          autoClose: 4000
        });
      } else {
        toast(t('openCaixa.toasts.success'), { type: "success" });
      }

      // Pequeno delay para garantir que o lançamento seja processado
      setTimeout(() => {
        refetch();
      }, 500);
    } catch (e) {
      if (e.response?.data?.msg) {
        toast(e.response.data.msg, { type: "error" });
      } else {
        toast(t('openCaixa.toasts.error'), { type: "error" });
      }
    } finally {
      setIsSubmiting(false);
    }
  };

  const criarLancamentoAutomatico = async ({ motivo, valor, tipo }) => {
    try {
      const empresa = localStorage.getItem('empresa');
      const empresaParse = JSON.parse(empresa);
      const idEmpresa = empresaParse.id_empresa;

      // Buscar o caixa recém-aberto
      const empresaResponse = await getEmpresa(userID, idEmpresa);

      process.env.REACT_APP_ISDEVELOP === 'true' && console.log("empresaResponse:",empresaResponse);
      if (!empresaResponse) {
        throw new Error(`Erro ao buscar empresa: ${empresaResponse}`);
      }

      const empresaData = empresaResponse;
      const caixaId = empresaData.data.empresa.caixa_aberto_id;

      const valorLancamento = tipo === 'sangria' ? -valor : valor;
      const tipoLabel = tipo === 'sangria' ? t('openCaixa.types.sangria') : t('openCaixa.types.suprimento');
      const descricao = `${tipoLabel} automático: ${motivo}`;

      process.env.REACT_APP_ISDEVELOP === 'true' && console.log({
        "id_empresa": idEmpresa,
        "caixa_id": caixaId,
        "descricao": descricao,
        "tipo_lancamento": "Dinheiro",
        "valor": valorLancamento,
        "createdBy": userID
      });

      await addLancamentoCaixa(
        idEmpresa,
        caixaId,
        descricao,
        "Dinheiro",
        valorLancamento,
        userID
      );

      toast(t('openCaixa.toasts.autoEntryRegistered', { type: tipoLabel }), { type: "info" });
    } catch (error) {
      process.env.REACT_APP_ISDEVELOP === 'true' && console.error('Erro ao criar lançamento automático:', error);
      toast(t('openCaixa.toasts.autoEntryError'), { type: "error" });
    }
  };

  const handleDiferencaConfirm = async (dadosDiferenca) => {
    setShowDiferencaModal(false);
    await abrirCaixa(pendingOpenData, dadosDiferenca);
    setPendingOpenData(null);
    setDiferencaData(null);
  };

  const handleDiferencaCancel = () => {
    setShowDiferencaModal(false);
    setPendingOpenData(null);
    setDiferencaData(null);
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose}>
      <div className="open-caixa-modal-container">
        {/* Header */}
        <div className="open-caixa-header-unique">
          <h3 className="open-caixa-title-unique">
            <FaCashRegister />
            {t('openCaixa.modalTitle')}
          </h3>
          <p className="open-caixa-subtitle-unique">
            {t('openCaixa.modalSubtitle')}
          </p>
          {saldoAnterior > 0 && (
            <div className="saldo-anterior-info">
              <small>{t('openCaixa.previousBalance', { amount: formatPriceWithSymbol(saldoAnterior) })}</small>
            </div>
          )}
        </div>

        {/* Content Card */}
        <div className="open-caixa-card-unique">
          <div className="open-caixa-form-header-unique">
            <div className="open-caixa-icon-unique">
              <FaCashRegister />
            </div>
            <h4 className="open-caixa-form-title-unique">{t('openCaixa.formTitle')}</h4>
          </div>

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {() => (
              <Form>
                <div className="open-caixa-input-group-unique">
                  <label htmlFor="amount" className="open-caixa-label-unique">
                    {t('openCaixa.inputLabel')}
                  </label>
                  <CurrencyInput
                    label="amount"
                    name="amount"
                    type="text"
                    placeholder={t('openCaixa.placeholder')}
                    className="open-caixa-input-unique"
                  />
                </div>

                <div className="open-caixa-actions-unique">
                  <button
                    className="open-caixa-btn-unique open-caixa-btn-cancel-unique"
                    type="button"
                    onClick={onClose}
                  >
                    {t('openCaixa.cancel')}
                  </button>
                  <button
                    className="open-caixa-btn-unique open-caixa-btn-confirm-unique"
                    type="submit"
                    disabled={isSubmiting}
                  >
                    {isSubmiting ? (
                      <>
                        <Loading
                          type="spin"
                          color="white"
                          height={16}
                          width={16}
                        />
                        {t('openCaixa.opening')}
                      </>
                    ) : (
                      t('openCaixa.submit')
                    )}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
      </Modal>

      {/* Modal de diferença de saldo */}
      {diferencaData && (
        <ModalDiferencaSaldo
          isOpen={showDiferencaModal}
          onClose={handleDiferencaCancel}
          onConfirm={handleDiferencaConfirm}
          diferenca={diferencaData.diferenca}
          saldoAnterior={diferencaData.saldoAnterior}
          saldoInformado={diferencaData.saldoInformado}
          tipo={diferencaData.tipo}
        />
      )}
    </>
  );
}
